import {useEffect, useState} from "react";
import {Scale} from "lucide-react";
import BankAccountSelector from "../../../../../../../components/specific/BankAccountSelector.tsx";
import {PrimaryButton} from "../../../../../../../components/general/PrimaryButton.tsx";
import {SecondaryButton} from "../../../../../../../components/general/SecondaryButton.tsx";
import {MoneyInput} from "../../../../../../../components/general/MoneyInput/MoneyInput.tsx";
import {centsToLocalizedString} from "../../../../../../../lib/Money.ts";
import type {CustomerBankAccount} from "../../../../../../../api/customerBankAccounts/CustomerBankAccount.ts";
import type {TFICDocument} from "../../../../../../../views/DocumentDrawer/types/Document";
import type {
    CreditCardStatementDocumentOption,
    CreditCardStatementLine,
    MissingDocumentRequest,
} from "../../types.ts";
import {CreditCardStatementLinesEditor} from "./CreditCardStatementLinesEditor.tsx";
import {CreditCardStatementPreviewPane} from "./CreditCardStatementPreviewPane.tsx";
import {CreditCardStatementSelector} from "./CreditCardStatementSelector.tsx";
import {
    canSubmitSettlement,
    differenceCents,
    newCreditCardStatementLine,
    type CreditCardSettlementFormPayload,
} from "./utils/creditCardSettlementUtils.ts";

interface CreditCardSettlementContentProps {
    settlementTransaction: MissingDocumentRequest;
    documents: CreditCardStatementDocumentOption[];
    creditCardAccounts: CustomerBankAccount[];
    isLoading: boolean;
    previewDocument: TFICDocument | null;
    isPreviewLoading: boolean;
    isSubmitting: boolean;
    onSelectDocument: (documentId: string) => void;
    onSubmit: (payload: CreditCardSettlementFormPayload) => void;
}

export function CreditCardSettlementContent({
    settlementTransaction,
    documents,
    creditCardAccounts,
    isLoading,
    previewDocument,
    isPreviewLoading,
    isSubmitting,
    onSelectDocument,
    onSubmit,
}: CreditCardSettlementContentProps) {
    const settlementSignedCents = settlementTransaction.total_amount_payable.signed_amount;

    const [selectedDocumentId, setSelectedDocumentId] = useState<string>("");
    const [selectedAccount, setSelectedAccount] = useState<CustomerBankAccount | null>(null);
    const [statementLines, setStatementLines] = useState<CreditCardStatementLine[]>([
        newCreditCardStatementLine(0),
    ]);
    const [nextKey, setNextKey] = useState<number>(1);

    useEffect(() => {
        if (selectedAccount === null && creditCardAccounts.length > 0) {
            setSelectedAccount(creditCardAccounts[0]);
        }
    }, [creditCardAccounts, selectedAccount]);

    const difference = differenceCents(settlementSignedCents, statementLines);
    const differenceLabel = centsToLocalizedString(Math.abs(difference));
    const canSubmit = canSubmitSettlement({
        selectedDocumentId,
        selectedAccount,
        statementLines,
        settlementSignedCents,
        isSubmitting,
        isLoading,
    });
    const disabledFeedback = difference !== 0
        ? "De som van de transacties moet gelijk zijn aan het afschrift totaal."
        : "Vul alle verplichte velden in.";
    const selectedAccountId = selectedAccount?.id ?? creditCardAccounts[0]?.id ?? "";

    function updateLine(key: number, patch: Partial<CreditCardStatementLine>) {
        setStatementLines((current) => current.map((line) => (line.key === key ? {...line, ...patch} : line)));
    }

    function addLine() {
        setStatementLines((current) => [...current, newCreditCardStatementLine(nextKey)]);
        setNextKey((key) => key + 1);
    }

    function removeLine(key: number) {
        setStatementLines((current) => (current.length <= 1 ? current : current.filter((line) => line.key !== key)));
    }

    function handleSelectDocument(documentId: string) {
        setSelectedDocumentId(documentId);
        onSelectDocument(documentId);
    }

    function handleConfirm() {
        if (!canSubmit || !selectedAccount) {
            return;
        }
        onSubmit({
            documentId: selectedDocumentId,
            creditCardAccountId: selectedAccount.id,
            statementLines,
        });
    }

    return (
        <div className="flex h-full min-h-[36rem] gap-8">
            <div className="flex w-[58%] flex-col overflow-y-auto border-r border-gray-200 pr-8">
                <div className="space-y-6">
                    <div>
                        <label className="mb-1 block text-sm font-medium text-gray-900">
                            Creditcard afschrift <span className="text-red-600">*</span>
                        </label>
                        {documents.length === 0 ? (
                            <p className="text-sm text-gray-500">
                                Geen creditcard afschriften gevonden. Upload eerst een afschrift via Documenten.
                            </p>
                        ) : (
                            <div className="w-96 max-w-full">
                                <CreditCardStatementSelector
                                    documents={documents}
                                    selectedDocumentId={selectedDocumentId}
                                    onSelect={handleSelectDocument}
                                    menuClassName="w-96 !max-h-80"
                                />
                            </div>
                        )}
                    </div>

                    <div>
                        <label className="mb-1 block text-sm font-medium text-gray-900">
                            Creditcard rekening <span className="text-red-600">*</span>
                        </label>
                        {isLoading ? (
                            <p className="text-sm text-gray-500">Rekeningen laden...</p>
                        ) : creditCardAccounts.length === 0 ? (
                            <p className="text-sm text-gray-500">
                                Geen creditcard rekeningen gevonden voor deze klant.
                            </p>
                        ) : (
                            <div className="[&_button]:w-96">
                                <BankAccountSelector
                                    selectedAccountId={selectedAccountId}
                                    bankAccounts={creditCardAccounts}
                                    onSelect={setSelectedAccount}
                                    menuClassName="w-96 !max-h-80"
                                />
                            </div>
                        )}
                    </div>

                    <div>
                        <label className="mb-1 block text-sm font-medium text-gray-900">
                            Afschrift totaal
                        </label>
                        <div className="w-96 max-w-full">
                            <MoneyInput
                                value={settlementTransaction.total_amount_payable}
                                disabled
                            />
                        </div>
                    </div>

                    <div>
                        <div className="mb-3 flex items-center justify-between">
                            <label className="block text-sm font-medium text-gray-900">Transacties</label>
                            <SecondaryButton title="Toevoegen" onClick={addLine} />
                        </div>
                        <CreditCardStatementLinesEditor
                            statementLines={statementLines}
                            onUpdateLine={updateLine}
                            onRemoveLine={removeLine}
                        />
                    </div>

                    {difference !== 0 && (
                        <div className="flex items-start gap-2 text-sm text-red-600">
                            <Scale className="h-5 w-5 shrink-0 text-red-600" aria-hidden />
                            <p>
                                Er is nog een verschil van{" "}
                                <span className="font-semibold">{differenceLabel}</span>. Vul alle
                                transacties in om het verschil naar nul te brengen.
                            </p>
                        </div>
                    )}

                    <div className="pt-2">
                        <PrimaryButton
                            title="Bevestigen"
                            className="px-6 py-2.5 text-base"
                            onClick={handleConfirm}
                            disabled={!canSubmit}
                            disabledFeedback={disabledFeedback}
                        />
                    </div>
                </div>
            </div>

            <div className="flex w-[42%] flex-col overflow-y-auto">
                <CreditCardStatementPreviewPane
                    previewDocument={previewDocument}
                    hasSelectedDocument={Boolean(selectedDocumentId)}
                    isPreviewLoading={isPreviewLoading}
                />
            </div>
        </div>
    );
}
